import React from 'react';
import {
  Box,
  Button,
  Modal,
  Typography,
  Paper,
  Fade,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

interface NextRoundModalProps {
  open: boolean;
  onClose: () => void;
  onCreateNewQuiz: () => void;
  onGoToDashboard: () => void;
}

const NextRoundModal: React.FC<NextRoundModalProps> = ({
  open,
  onClose,
  onCreateNewQuiz,
  onGoToDashboard,
}) => {
  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="next-round-modal"
      aria-describedby="next-round-modal-description"
    >
      <Fade in={open} timeout={500}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '90%',
            maxWidth: '500px',
            zIndex: 1000,
          }}
        >
          <Paper
            elevation={8}
            sx={{
              background:
                'linear-gradient(135deg, rgb(255, 255, 255) 0%, #e6f3ff 100%)',
              border: '2px solid rgb(73, 132, 234)',
              borderRadius: 3,
              boxShadow: '0 8px 32px rgba(255, 179, 217, 0.3)',
              overflow: 'hidden',
            }}
          >
            {/* Header */}
            <Box
              sx={{
                background: 'linear-gradient(135deg, #ff4da6 0%, #4682b4 100%)',
                p: 2,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                position: 'relative',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background:
                    'linear-gradient(45deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)',
                  pointerEvents: 'none',
                },
              }}
            >
              <Typography
                variant="h5"
                component="h2"
                sx={{
                  fontWeight: 700,
                  color: 'white',
                  textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                🚀 Ready for Next Round? ✨
              </Typography>
              <IconButton
                onClick={onClose}
                size="small"
                sx={{
                  color: 'white',
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.3)',
                    transform: 'scale(1.1)',
                  },
                  transition: 'all 0.2s ease-in-out',
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>

            {/* Content */}
            <Box sx={{ p: 3 }}>
              {/* Message */}
              <Box sx={{ mb: 3, textAlign: 'center' }}>
                <Typography
                  variant="h6"
                  sx={{
                    mb: 1.5,
                    fontWeight: 600,
                    color: '#5a4a6a',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1,
                  }}
                >
                  <span style={{ fontSize: '1.2rem' }}>🎉</span>
                  Round Completed!
                </Typography>

                <Box
                  sx={{
                    background:
                      'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                    borderRadius: 2,
                    p: 2,
                    mb: 2,
                    border: '2px solid rgba(73, 132, 234, 0.2)',
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      color: '#5a4a6a',
                      fontWeight: 500,
                      fontSize: '1.1rem',
                      lineHeight: 1.6,
                      mb: 1,
                    }}
                  >
                    Great job completing this round! 🎯
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#5f9ea0',
                      fontSize: '0.9rem',
                      fontStyle: 'italic',
                    }}
                  >
                    Ready to challenge yourself with another round?
                  </Typography>
                </Box>

                {/* Motivational Message */}
                <Typography
                  variant="body2"
                  sx={{
                    color: '#5f9ea0',
                    fontSize: '0.9rem',
                    fontStyle: 'italic',
                    mb: 2,
                  }}
                >
                  💪 Keep the momentum going and continue your learning journey!
                </Typography>
              </Box>

              {/* Action Buttons */}
              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  justifyContent: 'center',
                  pt: 1.5,
                  borderTop: '1px solid rgba(73, 132, 234, 0.2)',
                }}
              >
                <Button
                  variant="outlined"
                  onClick={onGoToDashboard}
                  size="medium"
                  sx={{
                    color: '#5f9ea0',
                    borderColor: '#5f9ea0',
                    px: 3,
                    py: 1,
                    borderRadius: 2,
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    '&:hover': {
                      borderColor: '#4682b4',
                      color: '#4682b4',
                      backgroundColor: 'rgba(70, 130, 180, 0.05)',
                      transform: 'translateY(-1px)',
                    },
                    transition: 'all 0.2s ease-in-out',
                  }}
                >
                  🏠 Dashboard
                </Button>
                <Button
                  variant="contained"
                  onClick={onCreateNewQuiz}
                  size="medium"
                  sx={{
                    background: 'linear-gradient(45deg, #ff4da6, #4682b4)',
                    color: 'white',
                    px: 3,
                    py: 1,
                    borderRadius: 2,
                    fontSize: '0.9rem',
                    fontWeight: 700,
                    textTransform: 'none',
                    letterSpacing: '0.5px',
                    boxShadow:
                      '0 6px 20px rgba(255, 102, 178, 0.4), 0 4px 15px rgba(70, 130, 180, 0.3)',
                    border: '2px solid rgba(255, 255, 255, 0.3)',
                    backdropFilter: 'blur(10px)',
                    '&:hover': {
                      background: 'linear-gradient(45deg, #ff66b2, #87ceeb)',
                      transform: 'translateY(-3px) scale(1.02)',
                      boxShadow:
                        '0 12px 30px rgba(255, 102, 178, 0.6), 0 8px 25px rgba(70, 130, 180, 0.4)',
                      border: '2px solid rgba(255, 255, 255, 0.5)',
                    },
                    '&:active': {
                      transform: 'translateY(-1px) scale(1.01)',
                    },
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                >
                  🎯 New Round
                </Button>
              </Box>
            </Box>
          </Paper>
        </Box>
      </Fade>
    </Modal>
  );
};

export default NextRoundModal;
